import { t } from './textos.js';

/**
 * Regras do Modo Versus que o tablet decide sozinho (Figma, quadros 24–28).
 *
 * Diferente das outras fases, esta é síncrona e depende de rede: cada
 * mensagem do chat é uma ida ao servidor, que conversa com a IA. A
 * pontuação é lançada pelo facilitador (RN-MV-16) — o tablet só exibe.
 */

const paragrafos = (texto) => String(texto ?? '').split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);

/** "urban_fighter" → "Urban Fighter". */
export function rotuloDoPerfil (perfil) {
    return String(perfil ?? '').split(/[_\s]+/).filter(Boolean)
        .map((palavra) => palavra[0].toUpperCase() + palavra.slice(1).toLowerCase()).join(' ');
}

/** "Raissa Pereira Lima" → "RAISSA" (RI-MV-07: primeiro nome sob cada nota). */
export function primeiroNome (nome) {
    return (String(nome ?? '').trim().split(/\s+/)[0] ?? '').toUpperCase();
}

/**
 * Quebra um título em duas linhas de tamanho parecido (rótulo das pastas
 * do seletor de materiais): "GUIA COMERCIAL ARGO X" → "GUIA COMERCIAL\nARGO X".
 */
export function quebrarEmDuas (titulo) {
    const palavras = String(titulo ?? '').trim().split(/\s+/);
    if (palavras.length < 3) return palavras.join(' ');
    let melhor = null;
    for (let i = 1; i < palavras.length; i++) {
        const [a, b] = [palavras.slice(0, i).join(' '), palavras.slice(i).join(' ')];
        const maior = Math.max(a.length, b.length);
        if (!melhor || maior < melhor.maior) melhor = { maior, texto: `${a}\n${b}` };
    }
    return melhor.texto;
}

/** Corta um texto longo numa frase curta para o cartão do case. */
export function resumir (texto, maximo = 70) {
    const limpo = String(texto ?? '').replace(/\s+/g, ' ').trim();
    if (limpo.length <= maximo) return limpo;
    return `${limpo.slice(0, maximo).replace(/\s+\S*$/, '').replace(/[\s.,;:!?]+$/, '')}...`;
}

/** Converte o catálogo do backend (`GET /versus/catalogo`) no formato das cenas. */
export function paraVersus (catalogo) {
    const tetos = catalogo?.tetos ?? {};
    return {
        // RN-MV-02 / RN-MV-03: duas rodadas, cada uma com um case distinto.
        rodadas: [...(catalogo?.rodadas ?? [])].sort((a, b) => a.ordem - b.ordem).map((rodada) => ({
            id: rodada.id,
            ordem: rodada.ordem,
            titulo: rodada.titulo,
            abertura: paragrafos(rodada.modal_inicio),
            caso: {
                nome: rodada.case.cliente_nome,
                idade: rodada.case.cliente_idade,
                perfil: rodada.case.perfil,
                // Título do cartão: o editado no painel; sem ele, o perfil formatado ("Urban Fighter").
                perfilRotulo: String(rodada.case.titulo ?? '').trim() || rotuloDoPerfil(rodada.case.perfil),
                contexto: rodada.case.contexto,
                objecao: rodada.case.objecao,
                missao: rodada.case.missao,
                // Resumos do cartão: campos opcionais; sem eles, corta-se o texto longo.
                perfilResumo: rodada.case.perfil_resumo ?? null,
                objecaoResumo: rodada.case.objecao_resumo ?? resumir(rodada.case.objecao)
            }
        })),
        criterios: [...(catalogo?.criterios ?? [])].sort((a, b) => a.ordem - b.ordem)
            .map((criterio) => ({ id: criterio.id, ordem: criterio.ordem, titulo: criterio.titulo })),
        tetos: {
            mensagens: tetos.mensagens_por_participante_por_rodada ?? null,
            caracteresMensagem: tetos.caracteres_por_mensagem ?? 1000,
            caracteresArgumento: tetos.caracteres_argumento_final ?? 2000
        },
        // RI-MV-03: materiais que podem ser anexados à conversa.
        materiais: (catalogo?.documentos_disponiveis ?? []).map((doc) => ({ id: doc.origem, titulo: doc.titulo })),
        // RN-MV-12: o servidor diz quem é o líder; na dúvida, ninguém escreve.
        lider: Boolean(catalogo?.lider)
    };
}

/** A tag que vai na mensagem ao anexar um material da biblioteca: é a origem dele na base da IA. */
export const tagDoMaterial = (materialId) => `material-${materialId}`;

/**
 * RI-MV-03: os anexos do chat são os materiais da biblioteca que o grupo já
 * liberou e que têm PDF — os mesmos do botão Materiais. `material` é o item da
 * biblioteca, para abrir o PDF no visualizador a partir do seletor.
 */
export function paraAnexos (biblioteca) {
    return (biblioteca ?? [])
        .filter((material) => material.desbloqueado && material.temArquivo)
        .map((material) => ({ id: tagDoMaterial(material.id), titulo: material.titulo, material }));
}

/** RI-MV-04: só envia mensagem com texto, dentro do teto de caracteres. */
export function mensagemValida (texto, maximo) {
    const limpo = String(texto ?? '').trim();
    return limpo.length > 0 && limpo.length <= maximo;
}

/**
 * `AC-INT-033` / `DP-INT-050`: decisão pura do toque em enviar. Acima do teto -> ignora; campo vazio
 * -> relê se o envio está bloqueado, senão ignora; texto válido -> envia (com `podeEnviar` falso o
 * `chat.enviar` já relê sem POST).
 */
export function decidirToqueDeEnvio ({ texto, podeEnviar, maximo }) {
    const limpo = String(texto ?? '').trim();
    if (limpo.length > maximo) return { acao: 'ignorar' };
    if (mensagemValida(limpo, maximo)) return { acao: 'enviar' };
    return { acao: podeEnviar ? 'ignorar' : 'reler' };
}

/** RN-MV-12 / RI-MV-11: Enviar só para o líder e com o argumento preenchido. */
export function podeEnviarArgumento (texto, { lider, maximo }) {
    return Boolean(lider) && mensagemValida(texto, maximo);
}

/**
 * Painel de pontuação (quadro 79): por rodada, o total e a nota de cada
 * critério. RN-MV-18/19/20: notas de 1 a 5, até 20 por rodada e 40 no
 * total. Os totais são os do servidor; só na falta deles o tablet soma.
 */
export function montarPlacarVersus ({ rodadas, criterios, avaliacao }) {
    const blocos = rodadas.map((rodada) => {
        const lancada = avaliacao?.rodadas?.find((r) => r.ordem === rodada.ordem) ?? null;
        const linhas = criterios.map((criterio) => ({
            rotulo: criterio.titulo,
            pontos: lancada?.notas?.find((n) => n.criterio_id === criterio.id)?.nota ?? null
        }));
        const soma = linhas.reduce((total, linha) => total + (linha.pontos ?? 0), 0);
        return { rotulo: t('versus.placar_rodada', { ordem: rodada.ordem }), total: lancada?.total ?? soma, linhas };
    });
    return { blocos, total: avaliacao?.total ?? blocos.reduce((soma, bloco) => soma + bloco.total, 0) };
}

const CODIGOS_DO_CHAT = [
    'teto_atingido', 'rodada_encerrada', 'rodada_fora_de_ordem', 'fase_nao_aberta', 'escopo_de_grupo_invalido'
];

const MENSAGENS_DO_CHAT = {
    teto_atingido: null,        // vem do catálogo de textos (`versus.erro_teto`), editável no painel
    rodada_encerrada: 'Esta rodada já foi encerrada; não dá mais para enviar mensagens.',
    rodada_fora_de_ordem: 'Esta rodada ainda não está liberada. Conclua a anterior primeiro.',
    fase_nao_aberta: 'O Modo Versus ainda não foi aberto pelo facilitador.',
    escopo_de_grupo_invalido: 'Este tablet não está vinculado a este grupo. Fale com o facilitador.',
    rede: 'Sem conexão com o servidor. Confira se a mensagem aparece na conversa antes de enviar de novo.',
    resposta_invalida: 'O servidor respondeu, mas a resposta veio inválida. Confira se a mensagem aparece na conversa antes de enviar de novo.',
    // Texto PROVISÓRIO (`DP-INT-053` pendente); não sugere conferir mensagem enviada (`AC-INT-034`).
    leitura: 'Não foi possível carregar a conversa. Verifique a conexão e toque em enviar para tentar novamente.',
    outro: 'Não foi possível enviar a mensagem agora. Tente de novo em instantes.'
};

/** `RN-INT-007`/`RN-INT-021`: a classe vem do `codigo`; `200` inválido é `resposta_invalida`; sem `status` é `rede`. */
export function classificarFalhaChat (falha) {
    if (CODIGOS_DO_CHAT.includes(falha?.codigo)) return falha.codigo;
    if (falha?.codigo === 'resposta_invalida') return 'resposta_invalida';
    return falha?.status === undefined ? 'rede' : 'outro';
}

export function mensagemDaFalhaChat (classe) {
    if (classe === 'teto_atingido') return t('versus.erro_teto');
    return MENSAGENS_DO_CHAT[classe] ?? MENSAGENS_DO_CHAT.outro;
}

const ESTADOS_DE_FALHA = ['recusada', 'nao_gravada', 'bloqueada'];

/** Mapa chave → texto dos avisos da cena (`DP-INT-044`): a chave de falha é `<estado>:<classe>` (`DP-INT-047`). */
export const TEXTOS_DO_AVISO = {
    ia_nao_chegou: 'Conversa atualizada.',
    conversa_recarregada: 'Conversa recarregada. Toque em enviar para mandar sua mensagem.',
    ...Object.fromEntries(ESTADOS_DE_FALHA.flatMap((estado) =>
        Object.keys(MENSAGENS_DO_CHAT).map((classe) => [`${estado}:${classe}`, MENSAGENS_DO_CHAT[classe]])))
};

/** Resolve o texto de um aviso pela chave; chave desconhecida cai na mensagem da classe. */
export function textoDoAviso (aviso) {
    return TEXTOS_DO_AVISO[aviso.chave] ?? mensagemDaFalhaChat(aviso.classe);
}

/**
 * `AC-INT-032`/`RN-INT-022`: decisão PURA do que a cena faz com o resultado de `chat.enviar`.
 * Com redesenho do histórico, `textoDaIa` é sempre nulo: a resposta vem do histórico (`DP-INT-048`).
 */
export function decidirResultadoDoEnvio (resultado) {
    const { estado, classe } = resultado;
    if (estado === 'respondida') {
        return { manterLinhaEnviada: true, textoDaIa: resultado.texto, aviso: null, devolverAoCampo: false, redesenharHistorico: false };
    }
    if (estado === 'ja_gravada') {
        return {
            manterLinhaEnviada: true,
            textoDaIa: null,
            aviso: resultado.texto ? null : { chave: 'ia_nao_chegou' },
            devolverAoCampo: false,
            redesenharHistorico: true
        };
    }
    if (estado === 'liberada') {
        return {
            manterLinhaEnviada: false,
            textoDaIa: null,
            aviso: { chave: 'conversa_recarregada' },
            devolverAoCampo: true,
            redesenharHistorico: true
        };
    }
    return {
        manterLinhaEnviada: false,
        textoDaIa: null,
        aviso: { chave: `${estado}:${classe}`, classe },
        devolverAoCampo: true,
        redesenharHistorico: false
    };
}

/**
 * RN-MV-08: o material anexado vale só para a mensagem enviada com ele. Gravada a mensagem
 * (respondida, ou já gravada antes), o anexo sai do campo e o próximo envio vai sem material,
 * a menos que o participante anexe de novo. Em qualquer falha o anexo fica, para reenviar a
 * mesma mensagem com ele. Pura, fora de `decidirResultadoDoEnvio`, cujo formato é contrato
 * (`AC-INT-032`).
 *
 * @param {{ estado: string }} resultado o retorno de `chat.enviar`
 * @returns {boolean}
 */
export function deveLimparAnexo (resultado) {
    return resultado?.estado === 'respondida' || resultado?.estado === 'ja_gravada';
}

/**
 * Lógica do chat fora da cena (`RN-INT-013`). O servidor é a fonte do histórico (`RN-INT-005`);
 * nunca há reenvio automático e, depois de uma queda de rede, o envio só é liberado quando a
 * releitura da conversa dá certo (`RN-INT-008`).
 */
export function criarChatVersus ({ fonte, progresso, turmaId, grupoId, participanteId, rodada }) {
    const endereco = { turmaId, grupoId, ordem: rodada.ordem, participanteId };
    const copiar = (lista) => lista.map((m) => ({ ...m }));
    const adotar = (lista) => {
        chat.mensagens = copiar(lista);
        progresso.substituirMensagens(rodada.id, lista);
    };

    const rede = { classe: 'rede', mensagem: mensagemDaFalhaChat('rede') };

    /** Uma releitura (GET): falhou, segue bloqueada; deu certo, adota e libera. Sem POST. */
    const reler = async () => {
        let relida;
        try {
            relida = await fonte.obterConversaVersus(endereco);
        } catch (falha) {
            console.error('[Versus] releitura da conversa falhou.', falha);
            chat.podeEnviar = false;
            // `AC-INT-034`: sem envio que caiu nunca houve POST; a falha é de leitura, não de rede.
            if (!chat.envioQueCaiu) return { estado: 'bloqueada', classe: 'leitura', mensagem: mensagemDaFalhaChat('leitura') };
            return { estado: 'bloqueada', ...rede };
        }
        adotar(relida);
        chat.podeEnviar = true;

        const envio = chat.envioQueCaiu;
        // `DP-INT-037`: sem envio que caiu (leitura inicial falha) só adota e libera.
        if (!envio) return { estado: 'liberada' };
        chat.envioQueCaiu = null;

        const novas = relida.slice(envio.conhecidas);
        const indice = novas.findIndex((m) => m.autor === 'eu' && m.texto === envio.texto);
        if (indice < 0) return { estado: 'nao_gravada', classe: envio.classe, mensagem: mensagemDaFalhaChat(envio.classe) };
        return { estado: 'ja_gravada', texto: novas.slice(indice + 1).find((m) => m.autor === 'ia')?.texto ?? null };
    };

    const chat = {
        mensagens: copiar(progresso.rodada(rodada.id).mensagens),
        podeEnviar: true,
        /** Envio cujo `POST` caiu (rede ou `200` inválido): `{ texto, conhecidas, classe }`, até a releitura apurar. */
        envioQueCaiu: null,

        async carregar () {
            try {
                adotar(await fonte.obterConversaVersus(endereco));
            } catch (falha) {
                console.error('[Versus] não foi possível ler a conversa.', falha);
                // `DP-INT-034`: a cópia local não aparece e o envio fica bloqueado.
                chat.mensagens = [];
                chat.podeEnviar = false;
                return { estado: 'falhou' };
            }
            chat.envioQueCaiu = null;
            chat.podeEnviar = true;
            return { estado: 'carregada' };
        },

        async enviar ({ texto, materiais = [] }) {
            if (!chat.podeEnviar) return reler();

            const conhecidas = chat.mensagens.length;
            try {
                const resposta = await fonte.enviarMensagemVersus({ ...endereco, texto, materiais });
                const novas = [{ autor: 'eu', texto, materiais }, { autor: 'ia', texto: resposta.texto, materiais: [] }];
                novas.forEach((m) => progresso.registrarMensagem(rodada.id, m));
                chat.mensagens = [...chat.mensagens, ...copiar(novas)];
                return { estado: 'respondida', texto: resposta.texto };
            } catch (falha) {
                const classe = classificarFalhaChat(falha);
                if (classe !== 'rede' && classe !== 'resposta_invalida') {
                    return { estado: 'recusada', classe, mensagem: mensagemDaFalhaChat(classe) };
                }
                chat.envioQueCaiu = { texto, conhecidas, classe };
            }

            chat.podeEnviar = false;
            return reler();
        }
    };
    return chat;
}

/** A rodada do catálogo a que o `destino` da sala aponta; `null` na espera da cédula. */
export function rodadaDoDestino (destino, rodadas) {
    return destino.etapa === 'rodada' ? rodadas.find((rodada) => rodada.ordem === destino.ordem) ?? null : null;
}

const MENSAGENS_DO_ARGUMENTO = {
    nao_lider: 'Somente o líder do grupo pode enviar o argumento.',
    argumento_ja_registrado: 'O argumento do grupo já foi enviado. Seguindo para a próxima etapa.',
    rodada_encerrada: 'Esta rodada já foi encerrada. Seguindo para a próxima etapa.',
    // A rodada só existe no servidor depois da 1ª mensagem do chat dela (`AC-MV-011`).
    rodada_fora_de_ordem: 'Converse com a IA nesta rodada antes de enviar o argumento.'
};

/**
 * `AC-INT-018`/`019`/`020`, `RN-INT-005`/`013`: a sala do Versus fora da cena e do `main.js`.
 * O servidor é a fonte do líder e das rodadas encerradas; `ehLider` mostra, nunca autoriza (o `POST`
 * do argumento é sempre tentado e o servidor decide). Criar não faz I/O: `entrar()` lê o estado e liga
 * os ouvintes (`pronto` = reidratação na reconexão; evento de rodada encerrada), `sair()` os cancela.
 * Nunca reenvia sozinha. `aoMudar` (opcional) avisa a cena quando o evento ou a reconexão alteram o estado.
 */
export function criarSalaVersus ({ fonte, rede, turmaId, grupoId, rodadas, aoMudar = () => {} }) {
    const identidade = { turmaId, grupoId };
    const ordens = rodadas.map((rodada) => rodada.ordem).sort((a, b) => a - b);
    const encerradas = new Set();
    // `V12`/`DP-MV-065`: ordens que o facilitador ainda não liberou (só a 2 pode ficar presa).
    const presas = new Set();
    const textosFinais = new Map();   // `IN18`: ordem -> texto final do evento (só string); nunca vem do estado
    let cancelamentos = [];
    const rascunhos = new Map();      // ordem -> último rascunho do líder lido do estado (só de rodada aberta)
    let lido = false;
    let ehLider = false;

    /** Uma leitura do estado; nunca lança. Só acumula `encerrada`: a releitura não desfaz o que o evento marcou. */
    const ler = async () => {
        let recebido;
        try {
            recebido = await fonte.obterEstadoVersus(identidade);
        } catch (falha) {
            console.error('[Versus] não foi possível ler o estado do grupo.', falha);
            return { estado: 'falhou' };
        }
        lido = true;
        ehLider = Boolean(recebido.ehLider);
        rascunhos.clear();
        presas.clear();
        for (const rodada of recebido.rodadas ?? []) {
            if (rodada.status === 'encerrada') encerradas.add(rodada.ordem);
            if (rodada.liberada === false) presas.add(rodada.ordem);
            if (typeof rodada.rascunho === 'string') rascunhos.set(rodada.ordem, rodada.rascunho);
        }
        return { estado: 'lido' };
    };

    const sala = {
        /** `true` depois de uma leitura bem-sucedida do estado; sem ela, a cena segue o progresso local. */
        get lida () { return lido; },
        get ehLider () { return lido && ehLider; },
        get podeEnviarArgumento () { return lido && ehLider; },
        get destino () {
            const ordem = ordens.find((o) => !encerradas.has(o));
            if (ordem === undefined) return { etapa: 'espera_cedula' };
            // `V12`: o case seguinte existe, mas espera o facilitador liberar.
            return presas.has(ordem) ? { etapa: 'aguardando_liberacao', ordem } : { etapa: 'rodada', ordem };
        },

        chatTravado (ordem) {
            return encerradas.has(ordem);
        },

        /** `AC-INT-065`: o texto final recebido no evento daquela `ordem`, ou `null`. Consulta pura, sem fallback. */
        textoFinal (ordem) {
            return textosFinais.get(ordem) ?? null;
        },

        /** O rascunho do líder daquela `ordem` na última leitura do estado, ou `null`. Reidrata quem reentrou na tela. */
        rascunho (ordem) {
            return rascunhos.get(ordem) ?? null;
        },

        async entrar () {
            if (cancelamentos.length === 0) {
                cancelamentos = [
                    rede.on('pronto', async () => {
                        await ler();
                        if (cancelamentos.length > 0) aoMudar();
                    }),
                    fonte.aoRodadaEncerradaVersus(({ ordem, texto }) => {
                        if (typeof texto === 'string') textosFinais.set(ordem, texto);
                        encerradas.add(ordem);
                        aoMudar();
                    }),
                    ...(fonte.aoSegundoCaseLiberadoVersus
                        ? [fonte.aoSegundoCaseLiberadoVersus(() => {
                            presas.delete(2);
                            aoMudar();
                        })]
                        : [])
                ];
            }
            return ler();
        },

        sair () {
            cancelamentos.forEach((cancelar) => cancelar());
            cancelamentos = [];
        },

        /**
         * `200` encerra a rodada localmente; `403` e `409` relêem o estado e a tela se ajusta. Qualquer
         * outra falha (rede, 5xx) sobe intacta — `DP-INT-062` ainda não decidiu o que fazer nela.
         */
        async enviarArgumento ({ ordem, texto }) {
            try {
                const gravado = await fonte.enviarArgumentoVersus({ ...identidade, ordem, texto });
                encerradas.add(ordem);
                return { estado: 'enviado', texto: gravado.texto };
            } catch (falha) {
                if (falha?.status === 403) {
                    await ler();
                    return { estado: 'recusada', classe: 'nao_lider', mensagem: MENSAGENS_DO_ARGUMENTO.nao_lider };
                }
                // Rodada ainda não aberta (sem conversa): não foi encerrada nem avança; fica na tela.
                if (falha?.status === 409 && falha.codigo === 'rodada_fora_de_ordem') {
                    return { estado: 'recusada', classe: 'rodada_fora_de_ordem', mensagem: MENSAGENS_DO_ARGUMENTO.rodada_fora_de_ordem };
                }
                if (falha?.status === 409) {
                    await ler();
                    return {
                        estado: 'rodada_encerrada',
                        classe: falha.codigo,
                        mensagem: MENSAGENS_DO_ARGUMENTO[falha.codigo] ?? MENSAGENS_DO_ARGUMENTO.rodada_encerrada
                    };
                }
                // Rede ou 5xx (`DP-INT-062`): o `POST` pode ter gravado. Relê o estado; rodada encerrada = o
                // envio chegou e a cena avança. Rodada aberta (ou releitura que também falhou): a falha sobe.
                // Nunca reenvia sozinha.
                if (falha?.status === undefined || falha.status >= 500) {
                    await ler();
                    if (encerradas.has(ordem)) {
                        return {
                            estado: 'rodada_encerrada',
                            classe: 'rodada_encerrada',
                            mensagem: MENSAGENS_DO_ARGUMENTO.rodada_encerrada
                        };
                    }
                }
                throw falha;
            }
        }
    };
    return sala;
}

/**
 * `AC-INT-065`/`DP-INT-098`: o texto que o não-líder confirma ao avançar. Vale o texto do evento quando
 * for **string** (`""` e só espaços incluídos, sem trim); senão, o último rascunho, como antes.
 */
export function escolherTextoFinal (textoDoEvento, rascunho) {
    return typeof textoDoEvento === 'string' ? textoDoEvento : rascunho;
}

const AVISO_SEM_LEITURA_DO_ARGUMENTO = 'Não conseguimos confirmar se você é o líder do grupo. Toque em TENTAR DE NOVO.';

/**
 * Papel do tablet na tela do argumento, decidido só pela sala: `indefinido` quando o estado do grupo
 * não foi lido (na dúvida ninguém escreve, mas a tela avisa em vez de ficar presa em silêncio),
 * `lider` ou `leitor` quando foi.
 */
export function papelNaTelaDoArgumento (sala) {
    if (!sala.lida) return { papel: 'indefinido', aviso: AVISO_SEM_LEITURA_DO_ARGUMENTO };
    return { papel: sala.podeEnviarArgumento ? 'lider' : 'leitor', aviso: null };
}

/** A reconexão (ou a releitura) mudou o papel? Então a cena se redesenha: promovido libera ENVIAR. */
export function mudouOPapelNaTela (papelNaTela, sala) {
    return papelNaTelaDoArgumento(sala).papel !== papelNaTela.papel;
}

const CHAVE = (participanteId) => `fiat-arcade:versus:v1:${participanteId}`;

const novaRodada = () => ({
    caseVisto: false,
    mensagens: [],            // { autor: 'eu' | 'ia', texto, materiais? } — RN-MV-07
    materiais: [],            // o material anexado à conversa: no máximo um (RN-MV-08)
    rascunho: '',             // RN-MV-14: argumento em produção
    argumento: null           // texto definitivo, depois do envio (RN-MV-15)
});

const novoEstado = () => ({ aberturaVista: false, rodadas: {}, avaliacao: null, concluido: false });

/** Andamento do participante na fase, salvo no tablet para sobreviver a recarga e troca de tela. */
/** Texto de uma resposta da IA para a área de transferência: sem as marcas de destaque `**`. */
export function textoParaCopiar (texto) {
    return String(texto ?? '').replaceAll('**', '');
}

/**
 * Copia um texto para a área de transferência. A API moderna só existe em contexto seguro
 * (https ou localhost); aberto por http na rede local, o tablet não a tem e o texto vai pelo
 * caminho antigo (`execCommand`). Devolve se copiou.
 *
 * @param {string} texto
 * @param {{ clipboard?: { writeText: (t: string) => Promise<void> }, documento?: Document }} [meios]
 * @returns {Promise<boolean>}
 */
export async function copiarTexto (texto, { clipboard = globalThis.navigator?.clipboard, documento = globalThis.document } = {}) {
    if (clipboard?.writeText) {
        try {
            await clipboard.writeText(texto);
            return true;
        } catch {
            // Sem permissão ou fora de contexto seguro: tenta o caminho antigo.
        }
    }
    if (!documento?.body) return false;
    const area = documento.createElement('textarea');
    area.value = texto;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    documento.body.append(area);
    area.select();
    let copiou = false;
    try {
        copiou = documento.execCommand('copy') === true;
    } catch {
        copiou = false;
    }
    area.remove();
    return copiou;
}

/** RN-MV-08 (ajustada em 30/10/2026): cada mensagem leva até dois materiais anexados. */
export const MAXIMO_DE_ANEXOS = 2;

/**
 * Os anexos que valem: sem repetição e no máximo `MAXIMO_DE_ANEXOS` — de uma lista maior, ficam
 * os últimos escolhidos. Aceita o que foi salvo no tablet antes da regra.
 */
export function limitarAnexos (materiais) {
    return [...new Set(materiais ?? [])].slice(-MAXIMO_DE_ANEXOS);
}

/**
 * Toque no seletor: marca o material, até dois; com dois já marcados, o mais antigo sai para o
 * novo entrar. Tocar num material já marcado desmarca só ele.
 */
export function alternarAnexo (escolhidos, id) {
    const atuais = limitarAnexos(escolhidos);
    if (atuais.includes(id)) return atuais.filter((outro) => outro !== id);
    return limitarAnexos([...atuais, id]);
}

export class ProgressoVersus {
    constructor (participanteId, { armazenamento = globalThis.localStorage } = {}) {
        this._chave = CHAVE(participanteId);
        this._armazenamento = armazenamento;
        this.estado = novoEstado();
        try {
            const bruto = armazenamento?.getItem(this._chave);
            if (bruto) this.estado = { ...novoEstado(), ...JSON.parse(bruto) };
        } catch { /* estado novo */ }
    }

    rodada (id) {
        this.estado.rodadas[id] ??= novaRodada();
        return this.estado.rodadas[id];
    }

    /** Rodada em andamento: a primeira sem argumento definitivo (RI-MV-13). */
    rodadaAtual (rodadas) {
        return rodadas.find((rodada) => this.rodada(rodada.id).argumento === null) ?? null;
    }

    marcarAberturaVista () {
        this.estado.aberturaVista = true;
        this._gravar();
    }

    marcarCaseVisto (id) {
        this.rodada(id).caseVisto = true;
        this._gravar();
    }

    registrarMensagem (id, mensagem) {
        this.rodada(id).mensagens.push(mensagem);
        this._gravar();
    }

    /** `AC-INT-017`: o histórico do servidor substitui o local, sem mescla. */
    substituirMensagens (id, mensagens) {
        this.rodada(id).mensagens = mensagens.map((m) => ({ ...m }));
        this._gravar();
    }

    /** RN-MV-08: o envio leva até dois documentos (`limitarAnexos`); aqui fica o que foi marcado. */
    anexar (id, materiais) {
        this.rodada(id).materiais = [...new Set(materiais)];
        this._gravar();
    }

    salvarRascunho (id, texto) {
        const rodada = this.rodada(id);
        if (rodada.argumento !== null) return;       // RN-MV-15: enviado é definitivo
        rodada.rascunho = texto;
        this._gravar();
    }

    /** RN-MV-15: confirmado o envio, o argumento da rodada não muda mais. */
    confirmarArgumento (id, texto) {
        const rodada = this.rodada(id);
        if (rodada.argumento !== null) return false;
        rodada.argumento = texto;
        rodada.rascunho = texto;
        this._gravar();
        return true;
    }

    registrarAvaliacao (avaliacao) {
        this.estado.avaliacao = avaliacao;
        this._gravar();
    }

    concluir () {
        this.estado.concluido = true;
        this._gravar();
    }

    reiniciar () {
        this.estado = novoEstado();
        this._gravar();
    }

    _gravar () {
        try { this._armazenamento?.setItem(this._chave, JSON.stringify(this.estado)); } catch { /* sem storage */ }
    }
}
